import { memoize, map } from 'lodash-es';
import React, { useState, useCallback } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { preventDefault, isStripeError } from '../../../../common/utils';
import { countries } from '../../../../common/countries';
import { states } from '../../../../common/states';
import { LayoutTitle, LayoutFooter } from '../Layout';
import { Alert } from '../../../../components/Alert';
import FormGroup from '../../../../components/FormGroup';
import Button from '../../../../components/Button';
import { TrialDisclaimerModal } from './TrialDisclaimerModal';
const stripeElementStyle = {
    style: {
        base: {
            '::placeholder': {
                color: '#717A8C',
            },
        },
    },
};
export function Payment({ fields, updateFields, onSubmit, onBack, onLoading }) {
    var _a;
    const updateField = useCallback(memoize((field) => (event) => {
        updateFields({ [field]: event.target.value });
    }), [updateFields]);
    const [errors, setErrors] = useState([]);
    const stripe = useStripe();
    const elements = useElements();
    const submitPayment = useCallback(preventDefault(async () => {
        try {
            if (!stripe || !elements) {
                throw new Error('Stripe has not yet loaded');
            }
            const numberElement = elements.getElement(CardNumberElement);
            if (!numberElement) {
                throw new Error('Stripe Error: Unable to get card number element');
            }
            onLoading(true);
            const paymentMethodResult = await stripe.createPaymentMethod({
                type: 'card',
                card: numberElement,
                billing_details: {
                    name: fields.cardName,
                    address: {
                        country: fields.cardCountry,
                        postal_code: fields.cardZipcode,
                        city: fields.cardCity,
                        line1: fields.cardAddress,
                        state: fields.cardState,
                    },
                },
            });
            if (paymentMethodResult.error) {
                throw paymentMethodResult.error;
            }
            updateFields({ paymentMethodId: paymentMethodResult.paymentMethod.id });
            onSubmit();
        }
        catch (error) {
            if (isStripeError(error)) {
                setErrors([error.message || 'Unknow Payment error']);
            }
            else {
                setErrors(['Unknown Error']);
                throw error;
            }
            window.scrollTo(0, 300);
        }
        finally {
            onLoading(false);
        }
    }), [onSubmit, onLoading, setErrors, updateFields]);
    return (React.createElement("div", { className: 'register-payment' },
        React.createElement(LayoutTitle, { text: "Payment Information" }),
        React.createElement("form", { className: 'payment-form-wrapper', onSubmit: submitPayment },
            React.createElement("div", { className: 'payment-form-wrapper__column' },
                React.createElement(Alert, { messages: errors, type: 'error' }),
                React.createElement("p", { className: 'payment-warning' }, "* Credit card information will be securely kept of file for future subscription payments."),
                React.createElement(FormGroup, null,
                    React.createElement("label", null,
                        "Name on Card ",
                        React.createElement("span", { className: 'form__required' })),
                    React.createElement("input", { onChange: updateField('cardName'), value: fields.cardName, type: 'text', className: 'form__control', name: "card_name", required: true })),
                React.createElement(FormGroup, { className: "payment-elements" },
                    React.createElement("div", { className: "payment-element" },
                        React.createElement("label", null,
                            "Card Number ",
                            React.createElement("span", { className: "form__required" })),
                        React.createElement(CardNumberElement, { options: stripeElementStyle })),
                    React.createElement("div", { className: "payment-element--small" },
                        React.createElement("label", null,
                            "Expiry Date ",
                            React.createElement("span", { className: "form__required" })),
                        React.createElement(CardExpiryElement, { options: stripeElementStyle })),
                    React.createElement("div", { className: "payment-element--small" },
                        React.createElement("label", null,
                            "CVC/CVV ",
                            React.createElement("span", { className: "form__required" })),
                        React.createElement(CardCvcElement, { options: stripeElementStyle }))),
                React.createElement(FormGroup, null,
                    React.createElement("label", null,
                        "Country ",
                        React.createElement("span", { className: 'form__required' })),
                    React.createElement("select", { value: fields.cardCountry, onChange: updateField('cardCountry'), className: 'form__control', required: true }, map(countries, country => React.createElement("option", { key: country.code, value: country.code }, country.name)))),
                React.createElement(FormGroup, null,
                    React.createElement("label", null,
                        "Address ",
                        React.createElement("span", { className: 'form__required' })),
                    React.createElement("input", { onChange: updateField('cardAddress'), value: fields.cardAddress, type: 'text', className: 'form__control', name: "address", required: true })),
                React.createElement(FormGroup, null,
                    React.createElement("label", null,
                        "City ",
                        React.createElement("span", { className: 'form__required' })),
                    React.createElement("input", { onChange: updateField('cardCity'), value: fields.cardCity, type: 'text', className: 'form__control', name: "city", required: true })),
                React.createElement("div", { className: 'form__row' },
                    React.createElement(FormGroup, null,
                        React.createElement("label", null,
                            "State ",
                            React.createElement("span", { className: 'form__required' })),
                        React.createElement("select", { value: fields.cardState, onChange: updateField('cardState'), className: 'form__control', required: true }, map(states, state => React.createElement("option", { key: state.code, value: state.code }, state.name)))),
                    React.createElement(FormGroup, null,
                        React.createElement("label", null,
                            "Zip Code ",
                            React.createElement("span", { className: 'form__required' })),
                        React.createElement("input", { onChange: updateField('cardZipcode'), pattern: "[0-9]*", maxLength: 12, value: fields.cardZipcode, type: 'text', className: 'form__control', name: "zipcode", required: true })))),
            React.createElement(LayoutFooter, { positionShouldChange: true },
                React.createElement(Button, { variant: "marketing-border", type: 'button', onClick: onBack }, "Back to review"),
                React.createElement(Button, { type: 'submit', variant: "marketing-solid" }, "Next"))),
        React.createElement(TrialDisclaimerModal, { shouldShow: ((_a = fields.selectedPlan) === null || _a === void 0 ? void 0 : _a.is_trial) || false })));
}
